import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { CostOfLivingReport } from '../../types/costOfLiving';
import {
  Loader2,
  CircleAlert,
  ArrowRight,
  Calculator,
  Bus,
  Car,
  Baby,
  Info,
  Wallet,
  Share2,
  Lightbulb,
  CheckCircle2,
  AlertTriangle,
  Home,
  ShoppingBasket,
  Zap,
  TramFront,
  Users,
  CalendarDays,
  Fingerprint,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';

type Lifestyle = 'frugal' | 'balanced' | 'comfort';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const getVerdictTone = (verdict: string) => {
  const v = verdict.toLowerCase();
  if (v.includes('strong') || v.includes('excellent') || v.includes('ideal')) {
    return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  }
  if (v.includes('mixed') || v.includes('consider')) {
    return 'bg-amber-100 text-amber-700 border-amber-200';
  }
  return 'bg-sky-100 text-sky-700 border-sky-200';
};

const CostOfLivingCountyPage: React.FC = () => {
  const { county } = useParams<{ county: string }>();
  const [data, setData] = useState<CostOfLivingReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [householdSize, setHouseholdSize] = useState(1);
  const [lifestyle, setLifestyle] = useState<Lifestyle>('frugal');
  const [eatOutOften, setEatOutOften] = useState(false);
  const [needsCar, setNeedsCar] = useState(false);
  const [needsChildcare, setNeedsChildcare] = useState(false);

  useEffect(() => {
    if (!county) {
      setError('County parameter is missing.');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getCostOfLivingReport?countyId=co-${county.toLowerCase()}`
        );
        const result = await response.json();
        if (!result.success || !result.data) {
          setError(`No cost of living report found for ${county}.`);
          return;
        }

        const report = result.data as CostOfLivingReport;
        setData(report);
        setHouseholdSize(report.calculator_defaults?.household_size || 1);
        setLifestyle('frugal');
        setEatOutOften(report.calculator_defaults?.eat_out_often || false);
        setNeedsCar(false);
        setNeedsChildcare(report.calculator_defaults?.needs_childcare || false);
      } catch (e) {
        setError('Unable to load cost of living report.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [county]);

  const calculator = useMemo(() => {
    if (!data) return null;

    const units = data.units;
    const housingBase = householdSize >= 3 ? units.rent_1bed_center * 1.3 : units.rent_1bed_center;
    const groceriesBase =
      householdSize >= 3 ? units.groceries_family_monthly : units.groceries_single_monthly;
    const utilityBase =
      units.utility_base_monthly * (householdSize >= 3 ? 1.35 : householdSize === 2 ? 1.15 : 1);
    const transportBase = units.monthly_bus_pass * Math.max(householdSize, 1);
    const mobileBase = units.mobile_internet_monthly * Math.min(householdSize, 2);
    const healthBase = units.health_insurance_monthly * Math.min(householdSize, 2);

    const lifestyleMultiplier = lifestyle === 'frugal' ? 0.88 : lifestyle === 'comfort' ? 1.18 : 1;
    const eatOutMultiplier = eatOutOften ? 1.35 : 1;
    const carCost = needsCar ? units.petrol_monthly : 0;
    const childcareCost = needsChildcare ? units.creche_weekly_full * 4.33 : 0;

    const housing = Math.round(housingBase * lifestyleMultiplier);
    const groceries = Math.round(groceriesBase * (lifestyle === 'frugal' ? 0.9 : 1.05));
    const utilities = Math.round(utilityBase);
    const transport = Math.round(transportBase + carCost);
    const social = Math.round(
      (units.dining_out_monthly * eatOutMultiplier + units.leisure_monthly) * lifestyleMultiplier
    );
    const digital = Math.round(mobileBase + healthBase);
    const childcare = Math.round(childcareCost);

    const total = housing + groceries + utilities + transport + social + digital + childcare;
    const diffNational = total - data.benchmarks.national_average;

    return {
      housing,
      groceries,
      utilities,
      transport,
      social,
      digital,
      childcare,
      total,
      diffNational,
      recommendedRent: Math.round(total * 0.35),
      estimatedBuyBudget: Math.round(total * 72),
    };
  }, [data, householdSize, lifestyle, eatOutOften, needsCar, needsChildcare]);

  if (loading) {
    return (
      <div className="min-h-[75vh] flex flex-col items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-4" />
        <p className="text-slate-500 font-bold">Loading cost of living calculator...</p>
      </div>
    );
  }

  if (error || !data || !calculator) {
    return (
      <div className="max-w-2xl mx-auto my-20 px-4">
        <div className="bg-white rounded-3xl p-10 shadow-xl border border-slate-100 text-center">
          <CircleAlert className="h-16 w-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black text-slate-900 mb-4">Report Unavailable</h1>
          <p className="text-slate-600 mb-8">{error}</p>
          <Link
            to="/cost-of-living"
            className="inline-block bg-emerald-600 text-white px-8 py-4 rounded-2xl font-black hover:bg-emerald-700 transition-all"
          >
            Browse Counties
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/cost-of-living/${county}`;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const compareDirection = calculator.diffNational >= 0 ? 'above' : 'below';
  const lifestyleScenarios = data.ai_enrichment?.lifestyle_scenarios || [];
  const maxScenarioMonthly = lifestyleScenarios.reduce(
    (max, scenario) => Math.max(max, scenario.monthly_estimate || 0),
    0
  );
  const sharePromoText = `See the ${data.county_name} cost of living breakdown on RentSmart.ie and compare rent, lifestyle, and affordability in Ireland.`;

  const toTitleCase = (countySlug?: string | null): string => {
    if (!countySlug) return '';

    return countySlug
      .toString()
      .trim()
      .replace(/[-_]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50 text-slate-900">
      <Helmet>
        <title>{data.seo_content.meta_title}</title>
        <meta name="description" content={data.seo_content.meta_desc} />
        <meta name="keywords" content={data.seo_content.keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(data.schema)}</script>
      </Helmet>

      <header className="bg-slate-900 text-white pt-12 pb-20 overflow-hidden relative">
        <div className="absolute top-0 right-0 h-80 w-80 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-cyan-500/20 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider mb-4">
                <Calculator className="h-4 w-4" /> 2026 Rentsmart.ie County Cost Calculator
              </div>
              <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
                {data.seo_content.h1}
              </h1>
              <p className="text-slate-300 text-lg max-w-3xl leading-relaxed mb-6">
                {data.seo_content.summary}
              </p>

              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="font-bold text-slate-400">Share:</span>
                <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                  <FacebookIcon size={30} round />
                </FacebookShareButton>
                <TwitterShareButton url={shareUrl} title={sharePromoText}>
                  <TwitterIcon size={30} round />
                </TwitterShareButton>
                <WhatsappShareButton url={shareUrl} title={sharePromoText}>
                  <WhatsappIcon size={30} round />
                </WhatsappShareButton>
                <LinkedinShareButton
                  url={shareUrl}
                  title={`Cost of Living in ${data.county_name} | RentSmart.ie`}
                  summary={sharePromoText}
                  source="RentSmart.ie"
                >
                  <LinkedinIcon size={30} round />
                </LinkedinShareButton>
              </div>
            </div>

            <div className="w-full lg:w-[370px] bg-white/10 rounded-3xl border border-white/20 p-6 backdrop-blur">
              <p className="text-xs uppercase tracking-widest text-slate-400 font-black mb-1">
                Estimated Monthly Total
              </p>
              <div className="text-4xl font-black text-emerald-400 mb-4">
                {formatCurrency(calculator.total)}
              </div>
              <p className="text-sm text-slate-300">
                {formatCurrency(Math.abs(calculator.diffNational))} {compareDirection} national
                benchmark.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 -mt-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <section className="lg:col-span-8 space-y-8">
            <div className="bg-white rounded-3xl mt-16 border border-slate-100 p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black mb-6">
                Cost of Living Calculator for {toTitleCase(county || data.county_name)}
              </h2>

              <div className="grid md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-black uppercase tracking-wider text-slate-500">
                    Household Size
                  </span>
                  <select
                    value={householdSize}
                    onChange={(event) => setHouseholdSize(Number(event.target.value))}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 bg-white"
                  >
                    <option value={1}>1 person</option>
                    <option value={2}>2 people</option>
                    <option value={3}>3 people</option>
                    <option value={4}>4 people</option>
                    <option value={5}>5+ people</option>
                  </select>
                </label>

                <label className="block">
                  <span className="text-xs font-black uppercase tracking-wider text-slate-500">
                    Lifestyle
                  </span>
                  <select
                    value={lifestyle}
                    onChange={(event) => setLifestyle(event.target.value as Lifestyle)}
                    className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 bg-white"
                  >
                    <option value="frugal">Frugal</option>
                    <option value="balanced">Balanced</option>
                    <option value="comfort">Comfort</option>
                  </select>
                </label>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => setEatOutOften((prev) => !prev)}
                  className={`rounded-xl border px-4 py-3 text-sm font-bold flex items-center gap-2 justify-center ${
                    eatOutOften
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                      : 'bg-white border-slate-200 text-slate-600'
                  }`}
                >
                  <Wallet className="h-4 w-4" /> Eat Out Often
                </button>

                <button
                  type="button"
                  onClick={() => setNeedsCar((prev) => !prev)}
                  className={`rounded-xl border px-4 py-3 text-sm font-bold flex items-center gap-2 justify-center ${
                    needsCar
                      ? 'bg-sky-50 border-sky-300 text-sky-700'
                      : 'bg-white border-slate-200 text-slate-600'
                  }`}
                >
                  {needsCar ? <Car className="h-4 w-4" /> : <Bus className="h-4 w-4" />} Need a Car
                </button>

                <button
                  type="button"
                  onClick={() => setNeedsChildcare((prev) => !prev)}
                  className={`rounded-xl border px-4 py-3 text-sm font-bold flex items-center gap-2 justify-center ${
                    needsChildcare
                      ? 'bg-amber-50 border-amber-300 text-amber-700'
                      : 'bg-white border-slate-200 text-slate-600'
                  }`}
                >
                  <Baby className="h-4 w-4" /> Childcare
                </button>
              </div>

              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  ['Housing', calculator.housing],
                  ['Groceries', calculator.groceries],
                  ['Utilities', calculator.utilities],
                  ['Transport', calculator.transport],
                  ['Lifestyle', calculator.social],
                  ['Digital & Health', calculator.digital],
                  ['Childcare', calculator.childcare],
                ]
                  .filter(([, value]) => Number(value) > 0)
                  .map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3"
                    >
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-black">
                        {label}
                      </p>
                      <p className="text-xl font-black text-slate-900">
                        {formatCurrency(Number(value))}
                      </p>
                    </div>
                  ))}
              </div>

              <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.16em] font-black text-emerald-700 mb-1">
                      Estimated Monthly Total
                    </p>
                    <p className="text-3xl md:text-4xl font-black text-emerald-800">
                      {formatCurrency(calculator.total)}
                    </p>
                  </div>
                  <div className="text-sm font-bold text-emerald-800/90">
                    {formatCurrency(Math.abs(calculator.diffNational))} {compareDirection} national average
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black mb-4">County Comparison</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-5">
                <div className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
                  <h3 className="font-black text-slate-900 mb-2">
                    {data.comparisons.vs_dublin.county}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {data.comparisons.vs_dublin.summary}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
                  <h3 className="font-black text-slate-900 mb-2">
                    {data.comparisons.vs_limerick.county}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {data.comparisons.vs_limerick.summary}
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                {data.insights.why_high_or_low}
              </p>
            </div>

            {data.ai_enrichment && (
              <div className="space-y-8">
                <div className="bg-slate-900 rounded-3xl border border-slate-800 p-6 md:p-8 text-white shadow-sm">
                  <div className="flex items-center gap-2 mb-4 text-emerald-400">
                    <Lightbulb className="h-5 w-5" />
                    <h2 className="text-2xl font-black">Local Market Lens</h2>
                  </div>
                  <p className="text-slate-200 leading-relaxed text-sm md:text-base">
                    {data.ai_enrichment.local_market_story}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-5">
                    <h3 className="font-black text-emerald-900 mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> Best For
                    </h3>
                    <ul className="space-y-2">
                      {data.ai_enrichment.moving_considerations.best_for.map((item, idx) => (
                        <li key={`${item}-${idx}`} className="text-sm text-emerald-900/90">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5">
                    <h3 className="font-black text-amber-900 mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" /> Watch Outs
                    </h3>
                    <ul className="space-y-2">
                      {data.ai_enrichment.moving_considerations.watch_out_for.map((item, idx) => (
                        <li key={`${item}-${idx}`} className="text-sm text-amber-900/90">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {data.ai_enrichment.county_uniqueness_signals?.length > 0 && (
                  <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 rounded-[2.5rem] border border-slate-700 p-6 md:p-8 shadow-xl">
                    <div className="absolute -top-16 -right-8 h-48 w-48 rounded-full bg-emerald-400/20 blur-3xl" />
                    <div className="relative z-10">
                      <p className="text-[10px] uppercase tracking-[0.2em] font-black text-emerald-300 mb-2">
                        Local Differentiators
                      </p>
                      <h3 className="text-2xl font-black mb-5 flex items-center gap-2 text-white">
                        <Fingerprint className="h-5 w-5 text-emerald-300" /> County Uniqueness
                        Signals
                      </h3>
                    </div>
                    <ul className="relative z-10 grid md:grid-cols-2 gap-3">
                      {data.ai_enrichment.county_uniqueness_signals.map((item, idx) => (
                        <li
                          key={`${item}-${idx}`}
                          className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur px-4 py-4 text-sm text-slate-100 leading-relaxed hover:bg-white/15 transition-all"
                        >
                          <span className="text-emerald-300 font-black mr-2">{`0${idx + 1}`}</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="bg-white rounded-[2.5rem] border border-slate-100 p-6 md:p-8 shadow-xl shadow-slate-200/40">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400 mb-2">
                    Narrative Breakdown
                  </p>
                  <h3 className="text-2xl font-black mb-5">Cost Breakdown Narrative</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      {
                        label: 'Housing',
                        text: data.ai_enrichment.cost_breakdown_narrative.housing,
                        icon: <Home className="h-4 w-4" />,
                      },
                      {
                        label: 'Groceries',
                        text: data.ai_enrichment.cost_breakdown_narrative.groceries,
                        icon: <ShoppingBasket className="h-4 w-4" />,
                      },
                      {
                        label: 'Utilities',
                        text: data.ai_enrichment.cost_breakdown_narrative.utilities,
                        icon: <Zap className="h-4 w-4" />,
                      },
                      {
                        label: 'Transport',
                        text: data.ai_enrichment.cost_breakdown_narrative.transport,
                        icon: <TramFront className="h-4 w-4" />,
                      },
                      {
                        label: 'Childcare',
                        text: data.ai_enrichment.cost_breakdown_narrative.childcare,
                        icon: <Baby className="h-4 w-4" />,
                      },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="group rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 hover:-translate-y-1 hover:shadow-xl transition-all"
                      >
                        <h4 className="font-black text-slate-900 mb-3 flex items-center gap-2">
                          <span className="p-2 rounded-xl bg-slate-900 text-white group-hover:bg-emerald-600 transition-colors">
                            {item.icon}
                          </span>
                          {item.label}
                        </h4>
                        <p className="text-sm text-slate-600 leading-relaxed font-medium">
                          {item.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {lifestyleScenarios.length > 0 && (
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 p-6 md:p-8 shadow-xl shadow-slate-200/40">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400 mb-2">
                      Scenario Studio
                    </p>
                    <h3 className="text-2xl font-black mb-5">Lifestyle Scenarios</h3>
                    <div className="space-y-4">
                      {lifestyleScenarios.map((scenario, idx) => (
                        <div
                          key={`${scenario.title}-${idx}`}
                          className="rounded-3xl border border-slate-200 bg-gradient-to-r from-white to-slate-50 p-5 md:p-6 hover:shadow-lg transition-all"
                        >
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <div className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
                                  <p className="text-[10px] uppercase tracking-wider font-black text-emerald-700">
                                    Best fit
                                  </p>
                                  <p className="text-sm font-semibold leading-relaxed">
                                    {scenario.fit_for}
                                  </p>
                                </div>
                                <span className="inline-flex text-[10px] uppercase tracking-wider font-black text-slate-500 px-2.5 py-1 rounded-full bg-slate-100 border border-slate-200">
                                  Scenario {idx + 1}
                                </span>
                              </div>
                              <h4 className="font-black text-slate-900 text-lg mb-2">
                                {scenario.title}
                              </h4>
                              <p className="text-sm text-slate-600 leading-relaxed font-medium mb-4">
                                {scenario.summary}
                              </p>
                              <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-sky-500"
                                  style={{
                                    width: `${Math.max(
                                      16,
                                      Math.round(
                                        ((scenario.monthly_estimate || 0) /
                                          Math.max(maxScenarioMonthly, 1)) *
                                          100
                                      )
                                    )}%`,
                                  }}
                                />
                              </div>
                            </div>
                            <div className="md:text-right">
                              <p className="text-[10px] uppercase tracking-[0.16em] font-black text-slate-400 mb-1">
                                Monthly Estimate
                              </p>
                              <p className="text-3xl font-black text-emerald-700">
                                {formatCurrency(scenario.monthly_estimate)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
                    <h3 className="text-xl font-black mb-4">Renter Strategy</h3>
                    <ul className="space-y-2">
                      {data.ai_enrichment.renter_strategy.map((item, idx) => (
                        <li key={`${item}-${idx}`} className="text-sm text-slate-700">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
                    <h3 className="text-xl font-black mb-4">Buyer Strategy</h3>
                    <ul className="space-y-2">
                      {data.ai_enrichment.buyer_strategy.map((item, idx) => (
                        <li key={`${item}-${idx}`} className="text-sm text-slate-700">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {data.ai_enrichment.persona_fit?.length > 0 && (
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 p-6 md:p-8 shadow-xl shadow-slate-200/40">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400 mb-2">
                      Audience Fit
                    </p>
                    <h3 className="text-2xl font-black mb-5 flex items-center gap-2">
                      <Users className="h-5 w-5 text-sky-600" /> Who This County Fits Best
                    </h3>
                    <div className="grid md:grid-cols-2 gap-5">
                      {data.ai_enrichment.persona_fit.map((item, idx) => (
                        <div
                          key={`${item.persona}-${idx}`}
                          className="group rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5 hover:-translate-y-1 hover:shadow-xl transition-all"
                        >
                          <p className="text-xs uppercase tracking-wider font-black text-slate-500 mb-2">
                            {item.persona}
                          </p>
                          <h4
                            className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-black mb-3 ${getVerdictTone(
                              item.verdict
                            )}`}
                          >
                            {item.verdict}
                          </h4>
                          <p className="text-sm text-slate-600 leading-relaxed">{item.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {data.ai_enrichment.monthly_action_plan?.length > 0 && (
                  <div className="bg-white rounded-[2.5rem] border border-slate-100 p-6 md:p-8 shadow-xl shadow-slate-200/40">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-black text-slate-400 mb-2">
                      Guided Execution
                    </p>
                    <h3 className="text-2xl font-black mb-5 flex items-center gap-2">
                      <CalendarDays className="h-5 w-5 text-violet-600" /> 30-Day Relocation Action
                      Plan
                    </h3>
                    <div className="space-y-4">
                      {data.ai_enrichment.monthly_action_plan.map((step, idx) => (
                        <div
                          key={`${step.week}-${idx}`}
                          className="relative rounded-3xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-5 pl-14"
                        >
                          <div className="absolute left-4 top-5 h-8 w-8 rounded-full bg-violet-600 text-white text-xs font-black flex items-center justify-center">
                            {idx + 1}
                          </div>
                          <p className="text-xs uppercase tracking-wider font-black text-violet-600 mb-1">
                            {step.week}
                          </p>
                          <h4 className="font-black text-slate-900 mb-1">{step.action}</h4>
                          <p className="text-sm text-slate-600 leading-relaxed">
                            {step.why_it_matters}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-5">
                    <h3 className="font-black text-emerald-900 mb-3">Area Pros</h3>
                    <ul className="space-y-2">
                      {data.ai_enrichment.area_pros_cons.pros.map((item, idx) => (
                        <li key={`${item}-${idx}`} className="text-sm text-emerald-900/90">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-rose-50 rounded-2xl border border-rose-200 p-5">
                    <h3 className="font-black text-rose-900 mb-3">Area Cons</h3>
                    <ul className="space-y-2">
                      {data.ai_enrichment.area_pros_cons.cons.map((item, idx) => (
                        <li key={`${item}-${idx}`} className="text-sm text-rose-900/90">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <PseoEmailCapture
              pageType="cost-of-living"
              pageSlug={data.slug}
              locationName={data.county_name}
              contextHeadline={`Track Cost Pressure Updates In ${data.county_name}`}
              contextReason={`Get Irish market updates for ${data.county_name} focused on household cost pressure, rent movement, and planning insights tailored by your renter/landlord/agent profile.`}
            />

            <div className="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 shadow-sm">
              <h2 className="text-2xl font-black mb-4">Frequently Asked Questions</h2>
              <div className="space-y-3">
                {data.seo_content.faq.map((item, idx) => (
                  <details
                    key={`${item.question}-${idx}`}
                    className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <summary className="font-bold text-slate-800 cursor-pointer">
                      {item.question}
                    </summary>
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>

            <div className="bg-amber-50 rounded-3xl border border-amber-200 p-6 md:p-8">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-700 mt-0.5" />
                <div>
                  <h2 className="text-xl font-black text-amber-900 mb-2">Disclaimer</h2>
                  <p className="text-sm text-amber-900/90 leading-relaxed">
                    This calculator provides indicative estimates for informational purposes only.
                    Actual living costs vary by exact location, household choices, subsidies,
                    utility contracts, commute patterns, and personal spending behaviour. RentSmart
                    does not provide financial, legal, tax, or investment advice.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <aside className="lg:col-span-4 space-y-6">
            <div className="sticky top-8 space-y-6">
              <div className="bg-emerald-700 rounded-3xl p-6 text-white shadow-xl">
                <h3 className="text-2xl font-black mb-3">Find Homes You Can Afford</h3>
                <p className="text-emerald-100 text-sm mb-5 leading-relaxed">
                  Based on your current calculator settings, a practical rent ceiling is around{' '}
                  {formatCurrency(calculator.recommendedRent)} per month.
                </p>
                <a
                  href={`/browse?listingIntent=rent&location=${encodeURIComponent(
                    data.county_name
                  )}&maxPrice=${calculator.recommendedRent}`}
                  className="no-underline inline-flex w-full items-center justify-between rounded-xl bg-white text-emerald-700 px-4 py-3 font-black"
                >
                  See Rental Matches <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div className="bg-sky-800 rounded-3xl p-6 text-white shadow-xl">
                <h3 className="text-2xl font-black mb-3">Buying Context</h3>
                <p className="text-sky-100 text-sm mb-5 leading-relaxed">
                  If your household budget supports it, explore homes up to about{' '}
                  {formatCurrency(calculator.estimatedBuyBudget)} as a directional benchmark.
                </p>
                <a
                  href={`/browse?listingIntent=sale&location=${encodeURIComponent(
                    data.county_name
                  )}&maxPrice=${calculator.estimatedBuyBudget}`}
                  className="no-underline inline-flex w-full items-center justify-between rounded-xl bg-white text-sky-800 px-4 py-3 font-black"
                >
                  Browse Homes <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <Link
                to="/cost-of-living"
                className="no-underline flex items-center justify-center gap-2 w-full py-4 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 font-black uppercase text-xs tracking-wider hover:border-emerald-500 hover:text-emerald-600 transition-all"
              >
                Compare Another County <ArrowRight className="h-4 w-4" />
              </Link>

            </div>
          </aside>
        </div>
      </main>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
              <Share2 className="h-5 w-5" />
            </div>
            <p className="text-sm text-slate-600 font-semibold">
              Share this county calculator with anyone planning a move, rental search, or budget
              reset.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
              <FacebookIcon size={34} round />
            </FacebookShareButton>
            <TwitterShareButton url={shareUrl} title={sharePromoText}>
              <TwitterIcon size={34} round />
            </TwitterShareButton>
            <WhatsappShareButton url={shareUrl} title={sharePromoText}>
              <WhatsappIcon size={34} round />
            </WhatsappShareButton>
            <LinkedinShareButton
              url={shareUrl}
              title={`Cost of Living in ${data.county_name} | RentSmart.ie`}
              summary={sharePromoText}
              source="RentSmart.ie"
            >
              <LinkedinIcon size={34} round />
            </LinkedinShareButton>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CostOfLivingCountyPage;
